import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { Icon } from '@components/icons';
import { usePrefersReducedMotion } from '@hooks';

const StyledPublicationsSection = styled.section`
  max-width: 900px;
  margin: 0 auto;
`;

const CollectionsWrapper = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// folder / tab row
const CollectionHeader = styled.button`
  position: relative;
  width: 100%;
  border: none;
  cursor: pointer;
  padding: 0.7rem 1.1rem;
  border-radius: 10px;
  text-align: left;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;

  background: ${({ $bg }) => $bg};
  color: #ffffff;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.25);
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  z-index: 5;

  &:hover,
  &:focus-visible {
    transform: translateX(-3px);
    box-shadow: 0 8px 14px rgba(0, 0, 0, 0.3);
    outline: none;
  }

  /* tab on the right */
  &::after {
    content: '';
    position: absolute;
    right: -18px;
    top: 50%;
    transform: translateY(-50%);
    width: 26px;
    height: 42px;
    border-radius: 0 12px 12px 0;
    background: ${({ $bg }) => $bg};
    box-shadow: 4px 0 8px rgba(0, 0, 0, 0.35);
  }

  .left {
    display: flex;
    align-items: center;
    gap: 0.6rem;
  }

  .icon-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.18);
    border-radius: 50%;
    width: 30px;
    height: 30px;

    svg {
      width: 18px;
      height: 18px;
    }
  }

  .titles {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  .main-title {
    font-size: var(--fz-sm);
    font-weight: 600;
    line-height: 1.2;
  }

  .subtitle {
    font-size: var(--fz-xxs);
    font-family: var(--font-mono);
    opacity: 0.9;
  }

  .chevron {
    font-size: var(--fz-md);
    transform: ${({ $open }) => ($open ? 'rotate(90deg)' : 'rotate(0deg)')};
    transition: transform 0.2s ease;
  }
`;

// UPDATED: Completely transparent background and neutral scrollbar
const CollectionBody = styled.div`
  padding: ${({ $open }) => ($open ? '0px 12px 12px 16px' : '0 12px')};
  max-height: ${({ $open }) => ($open ? '550px' : '0')}; 
  overflow-y: ${({ $open }) => ($open ? 'auto' : 'hidden')};
  
  transition: max-height 0.3s ease, padding 0.3s ease;
  background: transparent; 
  border-radius: 0 0 10px 10px;

  /* Custom Scrollbar - Changed color from $bg to neutral slate */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--slate); /* Neutral color to avoid purple tints */
    border-radius: 10px;
    border: 2px solid transparent;
    background-clip: padding-box;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

// UPDATED: Removed sticky background color
const Legend = styled.div`
  font-size: var(--fz-xxs);
  font-family: var(--font-mono);
  color: var(--slate);
  padding: 12px 0 8px 0;
  font-style: italic;
  background: transparent; /* Changed from var(--navy) to transparent */
  opacity: 0.7;
`;

const ItemsList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Item = styled.li`
  padding: 10px 0;
  font-size: var(--fz-xs);
  line-height: 1.4;
  color: var(--slate);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }

  .title {
    font-weight: 500;
    color: var(--lightest-slate);
    margin-bottom: 2px;
  }

  .authors,
  .venue {
    font-family: var(--font-mono);
    font-size: var(--fz-xxs);
    color: var(--light-slate);
  }

  .venue {
    color: var(--green);
    margin-top: 2px;
  }

  .year {
    display: inline-block;
    margin-top: 4px;
    font-size: var(--fz-xxs);
    opacity: 0.5;
  }

  .me-author {
    font-weight: 700;
    color: var(--green);
  }
  
  sup {
    font-size: 75%;
    line-height: 0;
    position: relative;
    vertical-align: baseline;
    top: -0.4em;
  }

  a {
    ${({ theme }) => theme.mixins.inlineLink};
  }
`;

const highlightAuthors = authors => {
  if (!authors) return null;
  const pattern = /(?:Zhao,\s*(?:J\.?|Junhan))|(?:Junhan\s+Zhao)|(?:J\.\s*Zhao)/gi;
  const parts = [];
  let lastIndex = 0;
  let match;
  const text = String(authors);

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(
        <span
          key={`pre-${match.index}`}
          dangerouslySetInnerHTML={{
            __html: text.slice(lastIndex, match.index),
          }}
        />,
      );
    }
    parts.push(
      <span key={match.index} className="me-author">
        {match[0]}
      </span>,
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(
      <span
        key="suffix"
        dangerouslySetInnerHTML={{
          __html: text.slice(lastIndex),
        }}
      />,
    );
  }
  return parts;
};

const collections = [
  {
    key: 'oncology',
    title: 'Data and Intelligence in Oncology',
    subtitle: 'Diagnotics, Prognostics, and Risk analytics',
    color: '#E6B098',
    items: [
      {
        title: 'A pathology foundation model for cancer diagnosis and prognosis prediction',
        authors: 'Xiyue Wang<sup>*</sup>, Junhan Zhao<sup>*</sup>, Eliana Marostica, Wei Yuan, Jietian Jin, Jiayu Zhang, Ruijiang Li, Hongping Tang, Kanran Wang, Yu Li, Fang Wang, Yulong Peng, Junyou Zhu, Jing Zhang, Christopher R. Jackson, Jun Zhang, Deborah Dillon, Nancy U. Lin, Lynette Sholl, Thomas Denize, David Meredith, Keith L. Ligon, Sabina Signoretti, Shuji Ogino, Jeffrey A. Golden, MacLean P. Nasrallah, Xiao Han, Sen Yang, Kun-Hsing Yu',
        venue: 'Nature',
        year: '2024',
        url: 'http://dx.doi.org/10.1038/s41586-024-07894-z',
      },
      {
        title: 'Uncertainty-aware ensemble of foundation models differentiates glioblastoma from its mimics',
        authors: 'Junhan Zhao<sup>*</sup>, Shih-Yen Lin<sup>*</sup>, Raphaël Attias, Liza Mathews, Christian Engel, Guillaume Larghero, Dmytro Vremenko, Ting-Wan Kao, Tsung-Hua Lee, Yu-Hsuan Wang, Cheng Che Tsai, Eliana Marostica, Ying-Chun Lo, David Meredith, Keith L. Ligon, Omar Arnaout, Thomas Roetzer-Pejrimovsky, Shih-Chieh Lin, Natalie NC Shih, Nipon Chaisuriya, David J. Cook, Jung-Hsien Chiang, Chia-Jen Liu, Adelheid Woehrer, Jeffrey A. Golden, MacLean P. Nasrallah, Kun-Hsing Yu',
        venue: 'Nature Communications',
        year: '2025',
        url: 'http://dx.doi.org/10.1038/s41467-025-64249-6',
      },
      {
        title: 'Counterfactual Bidirectional Co-Attention Transformer for Integrative Histology-Genomic Cancer Risk Stratification',
        authors: 'Zheyi Ji, Yongxin Ge, Chijioke Chukwudi, Kaicheng U, Sophia Meixuan Zhang, Yulong Peng, Junyou Zhu, Hisham Zaki, Xiang Zhang, Sen Yang, Xiyue Wang, Yijiang Chen, Junhan Zhao<sup>^</sup>',
        venue: 'IEEE Journal of Biomedical and Health Informatics',
        year: '2025',
        url: 'https://doi.org/10.1109/JBHI.2025.3548048',
      },
      {
        title: 'Pancancer outcome prediction via a unified weakly supervised deep learning model',
        authors: 'Wei Yuan, Yijiang Chen, Biyue Zhu, Sen Yang, Jun Zhang, Ning Mao, Jinxi Xiang, Yifan Li, Yuanfeng Ji, Xiangde Luo, Ke Zhang, Peng Xing, Shuxing Kang, Dan Xiao, Fang Wang, Jian Wu, Han Zhang, Hongping Tang, Hirdesh Maurya, Germán Corredor, Camilo Barrera, Yuchen Zhou, Kaustav Pandav, Junhan Zhao, Pranjal Jain, Leo Delasos, Jingjing Huang, Kaichao Yang, Ted N. Teknos, James Lewis, Shlomo Koyfman, Nathan A. Pennell, Kun-Hsing Yu, Xiao Han, Jian Zhang, Xiyue Wang, Anant Madabhushi',
        venue: 'Signal Transduction and Targeted Therapy',
        year: '2025',
        url: 'https://doi.org/10.1038/s41392-025-02374-w',
      },
      {
        title: 'Contrastive learning enhances fairness in pathology artificial intelligence systems',
        authors: 'Shih-Yen Lin, Pei-Chen Tsai, Fang-Yi Su, Chun-Yen Chen, Fuchen Li, Junhan Zhao, Yuk Yeung Ho, Tsung-Lu Michael Lee, Elizabeth Healey, Po-Jen Lin, Ting-Wan Kao, Dmytro Vremenko, Thomas Roetzer-Pejrimovsky, Lynette Sholl, Deborah Dillon, Nancy U. Lin, David Meredith, Keith L. Ligon, Ying-Chun Lo, Nipon Chaisuriya, David J. Cook, Adelheid Woehrer, Jeffrey Meyerhardt, Shuji Ogino, MacLean P. Nasrallah, Jeffrey A. Golden, Sabina Signoretti, Jung-Hsien Chiang, Kun-Hsing Yu<sup>^</sup>',
        venue: 'Cell Reports Medicine',
        year: '2025',
        url: 'https://www.cell.com/cell-reports-medicine/issue?pii=S2666-3791(24)X0013-0#fullCover',
      },
      {
        title: 'HiCervix: An Extensive Hierarchical Dataset and Benchmark for Cervical Cytology Classification',
        authors: 'De Cai<sup>*</sup>, Jie Chen<sup>*</sup>, Junhan Zhao<sup>*</sup>, Yuan Xue, Sen Yang, Wei Yuan, Min Feng, Haiyan Weng, Shuguang Liu, Yulong Peng, Junyou Zhu, Kanran Wang, Christopher Jackson, Hongping Tang, Junzhou Huang, Xiyue Wang',
        venue: 'IEEE Transactions on Medical Imaging',
        year: '2024',
        url: 'http://dx.doi.org/10.1109/tmi.2024.3419697',
      },
      {
        title: 'SAC-Net: Enhancing Spatiotemporal Aggregation in Cervical Histological Image Classification via Label-Efficient Weakly Supervised Learning',
        authors: 'Xiyue Wang, De Cai, Sen Yang, Yiming Cui, Junyou Zhu, Kanran Wang, Junhan Zhao<sup>^</sup>',
        venue: 'IEEE Transactions on Circuits and Systems for Video Technology',
        year: '2024',
        url: 'http://dx.doi.org/10.1109/TCSVT.2023.3294938',
      },
      {
        title: 'Active learning in computational pathology with noise detection empowered by loss-based prior and feature analysis',
        authors: 'Yujian Huang, Jinyang Li, Hui An, Tong Luo, Zheyi Ji, Yi Song, Huijun Liu, Kaicheng U, Sophia Meixuan Zhang, Junhan Zhao<sup>^</sup>, Yongxin Ge',
        venue: 'Biomedical Signal Processing and Control',
        year: '2025',
        url: 'http://dx.doi.org/10.1016/j.bspc.2025.107953',
      },
      {
        title: 'Deep Learning-Enabled Integration of Histology and Transcriptomics for Tissue Spatial Profile Analysis',
        authors: 'Yongxin Ge, Jiake Leng, Ziyang Tang, Kanran Wang, Kaicheng U, Sophia Meixuan Zhang, S. Han, Y. Zhang, Jinxi Xiang, Sen Yang, Xiang Liu, Y. Song, Xiyue Wang, Y. Li, Junhan Zhao<sup>^</sup>',
        venue: 'Research',
        year: '2025',
        url: 'http://dx.doi.org/10.34133/research.0568',
      },
      {
        title: 'Error-Robust and Label-Efficient Deep Learning for Understanding Tumor Microenvironment from Spatial Transcriptomics',
        authors: 'Jiake Leng, Y. Zhang, Xiang Liu, Yiming Cui, Junhan Zhao<sup>^</sup>, Yongxin Ge',
        venue: 'IEEE Transactions on Circuits and Systems for Video Technology',
        year: '2024',
        url: 'http://dx.doi.org/10.1109/TCSVT.2023.3301677',
      },
      {
        title: 'Federated attention consistent learning models for prostate cancer diagnosis and Gleason grading',
        authors: 'Fei Kong, Xiyue Wang, Jinxi Xiang, Sen Yang, Xinran Wang, Meng Yue, Jun Zhang, Junhan Zhao, Xiao Han, Yuhan Dong, Biyue Zhu, Fang Wang, Yueping Liu',
        venue: 'Computational and Structural Biotechnology Journal',
        year: '2024',
        url: 'https://doi.org/10.1016/j.csbj.2024.03.028',
      },
      {
        title: 'Association between ultra-processed food consumption and lung cancer risk: a population-based cohort study',
        authors: 'Kanran Wang, Junhan Zhao, Dingyi Yang, Mao Sun, Wei Zhou, Yongzhong Wu',
        venue: 'Thorax',
        year: '2025',
        url: 'https://doi.org/10.1136/thorax-2024-222100',
      },
      {
        title: 'Association of whole grain food consumption with lung cancer risk: a prospective cohort study',
        authors: 'Kanran Wang, Junhan Zhao, Dingyi Yang, Mao Sun, Yongzhong Wu, Wei Zhou',
        venue: 'Cancer Cell International',
        year: '2025',
        url: 'https://doi.org/10.1186/s12935-025-03634-z',
      },
      {
        title: 'Machine learning for cryosection pathology predicts the 2021 WHO classification of glioma',
        authors: 'MacLean P. Nasrallah<sup>*</sup>, Junhan Zhao<sup>*</sup>, Cheng Che Tsai<sup>*</sup>, David Meredith, Eliana Marostica, Keith L. Ligon, Jeffrey A. Golden, Kun-Hsing Yu',
        venue: 'Med',
        year: '2023',
        url: 'https://doi.org/10.1016%2Fj.medj.2023.06.002',
      },
    ],
  },
  {
    key: 'neurology',
    title: 'Data and Intelligence in Neurology',
    subtitle: 'Wearable devices; Developmental and degenerative neurological diseases',
    color: '#CC4452',
    items: [
      {
        title: 'Physical activity levels may impact on the risk of premature mortality in people with epilepsy',
        authors: 'Lingjie Fan<sup>*</sup>, Shengyi Liu<sup>*</sup>, Junhan Zhao<sup>*</sup>,<sup>^</sup>, Xiyue Wang, Yanyi Nie, Farah Qaderi, Qibo Su, Zihua He, Sen Yang, Tong Yi, Jiaqi Wang, Cairong Zhu, Josemir W. Sander, Dong Zhou, Tao Lin, Jinmei Li',
        venue: 'Nature Communications',
        year: '2025',
        url: 'http://dx.doi.org/10.1038/s41467-025-64805-0',
      },
      {
        title: 'Beyond the Clock: Exploring Multimodal Behavior Markers of Mild Cognitive Impairment in Older Adults during Clock Drawing Test',
        authors: 'Lingjie Fan, Junhan Zhao, Yongji Wu, Fengyi Wang, Xiyue Wang, Tao Lin',
        venue: 'Proceedings of the ACM on Interactive, Mobile, Wearable and Ubiquitous Technologies',
        year: '2025',
        url: 'http://dx.doi.org/10.1145/3770684',
      },
      {
        title: 'From physical activity patterns to cognitive status: development and validation of novel digital biomarkers for cognitive assessment in older adults',
        authors: 'Lingjie Fan<sup>*</sup>, Fengyi Wang<sup>*</sup>, Junhan Zhao<sup>*</sup>, Junjie Zhang, Yi-An Li, J. Tang, Tao Lin, Q. Wei',
        venue: 'International Journal of Behavioral Nutrition and Physical Activity',
        year: '2025',
        url: 'https://link.springer.com/article/10.1186/s12966-025-01706-x',
      },
      {
        title: 'Accelerometer-measured multidimensional physical activity patterns and physical functional decline: A prospective national cohort study of aging',
        authors: 'Lingjie Fan<sup>*</sup>, Junhan Zhao<sup>*</sup>, Jiaqi Wang, X. Zhou, Xiyue Wang, Shengyi Liu, Fengyi Wang, Q. Wei, Tao Lin',
        venue: 'Journals of Gerontology Series A',
        year: '2025',
        url: 'https://academic.oup.com/biomedgerontology/article-abstract/80/8/glaf158/8206306?redirectedFrom=fulltext',
      },
      {
        title: 'Predicting physical functioning status in older adults: insights from wrist accelerometer sensors and derived digital biomarkers of physical activity',
        authors: 'Lingjie Fan<sup>*</sup>, Junhan Zhao<sup>*</sup>, Yao Hu, Junjie Zhang, Xiyue Wang, Fengyi Wang, Mengyi Wu, Tao Lin',
        venue: 'Journal of the American Medical Informatics Association',
        year: '2024',
        url: 'http://dx.doi.org/10.1093/jamia/ocae224',
      },
      {
        title: 'PFIMPA: A Multimodal Approach to Predict Physical Function Impairment in Older Adults Using Physical Activity From Wrist-Worn Accelerometer',
        authors: 'Lingjie Fan<sup>*</sup>, Junjie Zhang<sup>*</sup>, Junhan Zhao<sup>*</sup>, Fengyi Wang, Tao Luo, Tao Lin',
        venue: 'IEEE Transactions on Instrumentation and Measurement',
        year: '2024',
        url: 'http://dx.doi.org/10.1109/tim.2024.3470051',
      },
    ],
  },
  {
    key: 'drug',
    title: 'Data and Intelligence in Drug Discovery',
    subtitle: 'Organoids, virtual staining, protein',
    color: '#723147',
    items: [
      {
        title: 'Prompt-based multimodal representation learning for drug repurposing',
        authors: 'Jinliang Liu, Kaicheng U, Dhruv Rana, Sophia Meixuan Zhang, Jiahui Yu, Sen Yang, Bo Jin, Xiyue Wang, Zongxin Yang, Hongping Tang, Junhan Zhao<sup>^</sup>',
        venue: 'Briefings in Bioinformatics',
        year: '2025',
        url: 'http://dx.doi.org/10.1093/bib/bbaf636',
      },
      {
        title: 'PhaseFIT: live-organoid phase-fluorescent image transformation via generative AI',
        authors: 'Junhan Zhao<sup>*</sup>, Xiyue Wang<sup>*</sup>, Junyou Zhu<sup>*</sup>, Chijioke Chukwudi, Andrew Finebaum, Jun Zhang, Sen Yang, Shijie He, Nima Saeidi',
        venue: 'Light Science and Applications',
        year: '2023',
        url: 'https://doi.org/10.1038/s41377-023-01296-y',
      },
      {
        title: 'AI-Driven Acceleration of Fluorescence Probe Discovery',
        authors: 'Xuefeng Jiang,  Yanbo Li,  Xue Tian,  Sen Yang,  Ruina Luo,  Cenxing Zhou,  Yuxuan Liu,  Jingying Hu,  Sen Feng,  Lu Gan,  Chongzhao Ran,  Kun-Hsing Yu,  Junhan Zhao,  Xiao Han,  Xuan Zhai,  Yuntao Jia,  Jiapei Dai,  Xiyue Wang,  Biyue Zhu',
        venue: 'Advanced Science',
        year: '2025',
        url: 'https://doi.org/10.1002/advs.202515604',
      },
      {
        title: 'Building consensus on the application of organoid-based drug sensitivity testing in cancer precision medicine and drug development',
        authors: 'Dongxi Xiang, Aina He, Rong Zhou, Yonggang Wang, Xiuying Xiao, Ting Gong, Wenyan Kang, Xiaolin Lin, Xiaochen Wang, Lianxin Liu, Ye-Guang Chen, Shaorong Gao, Yingbin Liu',
        venue: 'Theranostics',
        year: '2024',
        url: 'http://dx.doi.org/10.7150/thno.96027',
      },
      {
        title: 'Large Context, Deeper Insights: Harnessing Large Language Models for Advancing Protein-Protein Interaction Analysis',
        authors: 'Kaicheng U, Sophia Meixuan Zhang, S. Pokharel, P. Pratyush, Farah Qaderi, Dongfang Liu, Junhan Zhao<sup>^</sup>, D.B. KC, Siwei Chen',
        venue: 'Methods in Molecular Biology',
        year: '2025',
        url: 'https://link.springer.com/protocol/10.1007/978-1-0716-4623-6_15',
      },
      {
        title: 'DISPROTBENCH: A Disorder-Aware, Task-Rich Benchmark for Evaluating Protein Structure Prediction in Realistic Biological Contexts',
        authors: 'Xinyue Zeng, Tuo Wang, Adithya Kulkarni, Alexander Lu, Alexandra Ni, Phoebe Xing, Junhan Zhao<sup>^</sup>, Siwei Chen, Dawei Zhou',
        venue: 'Arxiv',
        year: '2025',
        url: 'https://doi.org/10.48550/arXiv.2507.02883',
      },
    ],
  },
  {
    key: 'general',
    title: 'General AI and HCI',
    subtitle: 'Computer graphics, computer vision, tooling',
    color: '#31152B',
    items: [
      {
        title: 'A Generative Foundation Model for Chest Radiography',
        authors: 'Yuanfeng Ji, Dan Lin, Xiyue Wang, Lu Zhang, Wenhui Zhou, Chongjian Ge, Ruihang Chu, Xiaoli Yang, Junhan Zhao, Junsong Chen, Xiangde Luo, Sen Yang, Jin Fang, Ping Luo, Ruijiang Li',
        venue: 'Arxiv',
        year: '2025',
        url: 'https://doi.org/10.48550/arXiv.2509.03903',
      },
      {
        title: 'Towards General-Purpose Video Reconstruction through Synergy of Grid-Splicing Diffusion and Large Language Models',
        authors: 'Jinliang Liu, Jianwei Zhang, Sen Yang, Jinxi Xiang, Xiyue Wang, Jieqiong Zhao, Zongxin Yang, Junhan Zhao<sup>^</sup>',
        venue: 'IEEE Transactions on Circuits and Systems for Video Technology',
        year: '2025',
        url: 'http://dx.doi.org/10.1109/TCSVT.2025.3545795',
      },
      {
        title: 'Rethinking Copy-Paste for Consistency Learning in Medical Image Segmentation',
        authors: 'Senlong Huang, Yongxin Ge, Dongfang Liu, Mingjian Hong, Junhan Zhao, Alexander C. Loui',
        venue: 'IEEE Transactions on Image Processing',
        year: '2025',
        url: 'http://dx.doi.org/10.1109/TIP.2025.3536208',
      },
      {
        title: 'M2PT: Multimodal Prompt Tuning for Zero-shot Instruction Learning',
        authors: 'Taowen Wang, Yiyang Liu, James Chenhao Liang, Junhan Zhao, Yiming Cui, Yuning Mao, Shaoliang Nie, Jiahao Liu, Fuli Feng, Zenglin Xu, Cheng Han, Lifu Huang, Qifan Wang, Dongfang Liu',
        venue: 'EMNLP 2024',
        year: '2024',
        url: 'http://dx.doi.org/10.18653/v1/2024.emnlp-main.218',
      },
      {
        title: 'Advancing hierarchical neural networks with scale-aware pyramidal feature learning for medical image dense prediction',
        authors: 'Xiang Liu, James Chenhao Liang, Jianwei Zhang, Zhenyu Qian, Peng Xing, Tao Chen, Sen Yang, Chijioke Chukwudi, Linyan Qiu, Dongfang Liu, Junhan Zhao<sup>^</sup>',
        venue: 'Computer Methods and Programs in Biomedicine',
        year: '2025',
        url: 'https://doi.org/10.1016/j.cmpb.2025.108705',
      },
      {
        title: 'A systematic evaluation of computational methods for cell segmentation',
        authors: 'Yuxing Wang<sup>*</sup>, Junhan Zhao<sup>*</sup>, Hongye Xu, C. Han, Z. Tao, Dawei Zhou, Tong Geng, Dongfang Liu, Zhicheng Ji',
        venue: 'Briefings in Bioinformatics',
        year: '2024',
        url: 'https://doi.org/10.1093/bib/bbae407',
      },
      {
        title: 'Mesoscopic structure graphs for interpreting uncertainty in non-linear embeddings',
        authors: 'Junhan Zhao<sup>*</sup>, Xiang Liu<sup>*</sup>, Hongping Tang<sup>*</sup>, Xiyue Wang, Sen Yang, Dongfang Liu, Yijiang Chen, Yingjie Victor Chen',
        venue: 'Computers in Biology and Medicine',
        year: '2024',
        url: 'http://dx.doi.org/10.1016/j.compbiomed.2024.109105',
      },
      {
        title: 'Colorslope: a balanced visualization of overview and details on ranks over time',
        authors: 'H. Wang, X. Jiang, A. Nagarajan, X. Guo, L. Ding, D. Wan, Junhan Zhao, Yingjie Victor Chen',
        venue: 'Visual Intelligence',
        year: '2023',
        url: 'https://doi.org/10.1007/s44267-023-00008-9',
      },
      {
        title: 'Radiance Field Learners As UAV First-Person Viewers',
        authors: 'Liqi Yan, Qifan Wang, Junhan Zhao, Qiang Guan, Zheng Tang, Jianhui Zhang, Dongfang Liu',
        venue: 'Lecture Notes in Computer Science',
        year: '2025',
        url: 'https://arxiv.org/abs/2408.05533',
      },
      {
        title: 'Memorizing All for Implicit Discourse Relation Recognition',
        authors: 'Kashif Munir, Hongxiao Bai, Hai Zhao, Junhan Zhao',
        venue: 'ACM Transactions on Asian and Low-Resource Language Information Processing',
        year: '2021',
        url: 'https://doi.org/10.1145%2F3485016',
      },
      {
        title: 'Phoenixmap: An Abstract Approach to Visualize 2D Spatial Distributions',
        authors: 'Junhan Zhao, Xiang Liu, Chanjuan Guo, Zhenyu Qian, Yingjie Victor Chen',
        venue: 'IEEE Transactions on Visualization and Computer Graphics',
        year: '2021',
        url: 'https://doi.org/10.1109/TVCG.2019.2945960',
      },
      {
        title: 'ProtoViewer: Visual Interpretation and Diagnostics of Deep Neural Networks with Factorized Prototypes',
        authors: 'Junhan Zhao, Zeng Dai, Panpan Xu, Liu Ren',
        venue: 'IEEE VIS',
        year: '2020',
        url: 'https://doi.org/10.1109%2Fvis47514.2020.00064',
      },
      {
        title: 'Click or not: different mouseover effects may affect clicking-through rate while browsing interactive information visualization',
        authors: 'Keyuan Zhou, Wenjie Wu, Junhan Zhao, Mingran Li, Zhenyu Qian, Yingjie Victor Chen',
        venue: 'Journal of Visualization',
        year: '2019',
        url: 'https://doi.org/10.1007%2Fs12650-019-00603-3',
      },
      {
        title: 'CareerVis: Hierarchical Visualization of Career Pathway Data',
        authors: 'Mingran Li, Wenjie Wu, Junhan Zhao, Keyuan Zhou, David Perkis, Timothy N. Bond, Kevin Mumford, David Hummels, Yingjie Victor Chen',
        venue: 'IEEE Computer Graphics and Applications',
        year: '2018',
        url: 'https://doi.org/10.1109%2Fmcg.2018.2874514',
      },
      {
        title: 'Deep CNN-Based Methods to Evaluate Neighborhood-Scale Urban Valuation Through Street Scenes Perception',
        authors: 'Junhan Zhao, Xiang Liu, Yanqun Kuang, Yingjie Victor Chen, Baijian Yang',
        venue: 'IEEE DSC',
        year: '2018',
        url: 'https://doi.org/10.1109%2Fdsc.2018.00012',
      },
      {
        title: 'Predicting the Distribution of Ailanthus altissima Using Deep Learning-Based Analysis of Satellite Imagery',
        authors: 'Ruohan Gao, Zipeng Song, Junhan Zhao<sup>^</sup>, Yingnan Li<sup>^</sup>',
        venue: 'Symmetry',
        year: '2025',
        url: 'https://doi.org/10.3390/sym17030324',
      },
    ],
  },
];

const Publications = () => {
  const [openKey, setOpenKey] = useState(null);
  const revealTitle = useRef(null);
  const revealSection = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) return;
    if (revealTitle.current) sr.reveal(revealTitle.current, srConfig());
    if (revealSection.current) sr.reveal(revealSection.current, srConfig());
  }, [prefersReducedMotion]);

  const toggle = key => {
    setOpenKey(prev => (prev === key ? null : key));
  };

  return (
    <StyledPublicationsSection id="publications" ref={revealSection}>
      <h2 className="numbered-heading" ref={revealTitle}>
        Publications
      </h2>

      <CollectionsWrapper>
        {collections.map(col => {
          const isOpen = openKey === col.key;
          return (
            <div key={col.key}>
              <CollectionHeader
                type="button"
                onClick={() => toggle(col.key)}
                aria-expanded={isOpen}
                aria-controls={`pub-collection-${col.key}`}
                $bg={col.color}
                $open={isOpen}>
                <div className="left">
                  <div className="icon-wrap">
                    <Icon name="Article" />
                  </div>
                  <div className="titles">
                    <span className="main-title">{col.title}</span>
                    {col.subtitle && (
                      <span className="subtitle">{col.subtitle}</span>
                    )}
                  </div>
                </div>
                <span className="chevron">▶</span>
              </CollectionHeader>

              <CollectionBody 
                id={`pub-collection-${col.key}`} 
                $open={isOpen}
                $bg={col.color}>
                
                <Legend>first (*) or corresponding (^)</Legend>

                <ItemsList>
                  {col.items.map((item, i) => (
                    <Item key={i}>
                      <div className="title">
                        {item.url ? (
                          <a href={item.url} target="_blank" rel="noreferrer">
                            {item.title}
                          </a>
                        ) : (
                          item.title
                        )}
                      </div>
                      {item.authors && (
                        <div className="authors">
                          {highlightAuthors(item.authors)}
                        </div>
                      )}
                      {item.venue && <div className="venue">{item.venue}</div>}
                      <span className="year">{item.year}</span>
                    </Item>
                  ))}
                </ItemsList>
              </CollectionBody>
            </div>
          );
        })}
      </CollectionsWrapper>
    </StyledPublicationsSection>
  );
};

export default Publications;